import InfoIcon from '@diligentcorp/atlas-react-icons/dist/esm/lens/Info.js';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  Stack,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import React from 'react';

export interface InfoModalLink {
  label: string;
  /**
   * When omitted the row renders as non-clickable placeholder text (icon +
   * label) — used while real help-centre URLs are pending. Once an `href` is
   * provided the row becomes a real anchor opening in a new tab.
   */
  href?: string;
}

export interface InfoModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  links: InfoModalLink[];
  bodyText: string;
}

const TITLE_ID = 'atlas-info-modal-title';

/**
 * Atlas Lens info modal. Mount inside `<AtlasMuiThemeProvider>`.
 *
 * No custom typography / spacing — everything resolves from the Atlas theme
 * defaults applied to MUI primitives.
 */
export const InfoModal: React.FC<InfoModalProps> = ({
  open,
  onClose,
  title,
  links,
  bodyText,
}) => (
  <Dialog open={open} onClose={onClose} aria-labelledby={TITLE_ID}>
    <DialogTitle id={TITLE_ID}>
      {title}
      <IconButton
        onClick={onClose}
        aria-label="Close"
        data-testid="info-modal-close"
      >
        <CloseIcon />
      </IconButton>
    </DialogTitle>
    <DialogContent>
      <Stack spacing={1}>
        {links.map((link) =>
          link.href ? (
            <Link
              key={(link.href ?? '') + link.label}
              href={link.href}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}
            >
              <InfoIcon />
              <span>{link.label}</span>
            </Link>
          ) : (
            <Link
              key={link.label}
              component="span"
              underline="always"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1,
                cursor: 'default',
              }}
              data-testid="info-modal-link-placeholder"
            >
              <InfoIcon />
              <span>{link.label}</span>
            </Link>
          ),
        )}
        <Typography>{bodyText}</Typography>
      </Stack>
    </DialogContent>
  </Dialog>
);
